import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CardLoanTasksProps } from 'components/Atoms/Cards/CardLoanTasks/CardLoanTasks';
import { useGetUserLoggin } from 'services/user/querys';
import { Task, TaskState } from 'gql/graphql';
import { useGetTasksByLoan } from 'services/task/querys';
import { Phases, PhasesLabel } from './LoanPhases.constanst';
import { useGetLoanByOwner } from 'services/loan/querys';
import useCardEntityGuarantor from './components/EntityGuarantorCreation/useCardEntityGuarantor';
import useCardMinimumLiquidity from './components/MinimumLiquidity/useCardMinimumLiquidity';
import useCardTrackRecord from './components/TrackRecord/useCardTrackRecord';
import useCardEntityDocs from './components/EntityDocs/useCardEntityDocs';
import useCardInvestment from './components/InvestmentExperienceVerification/useCardInvestment';
import useCardConstruction from './components/ConstructionBudget/useCardConstruction';
import useCardTitleCompany from './components/TitleCompany/useCardTitleCompany';
import useCardInsurance from './components/Insurance/useCardInsurance';
import useCardPurchase from './components/Purchase/useCardPurchase';
import useCardPayoff from './components/Payoff/useCardPayoff';
import useCardPropertyAccess from './components/PropertyAccessDetails/useCardPropertyAccess';
import useCardPayAppraisal from './components/PayAppraisal/useCardPayAppraisal';
import useCardDSCRQuestionsAgreements from './components/DscrQuestionsAgreements/useCardDscrQuestionsAgreements';
import { RulesTasksCards } from './RulesTaskCards.constanst';
import useCardNewTask from './components/NewTask/useCardNewTask';
import { updateLoanContextInSentry } from 'sentry/contexts/loanContext';
import { ModuleKeys } from 'services/task/modules-keys.constanst';

const useDashboard = (
  ownerSelect?: string
) => {
  let { loanId: loanIdParam }: any = useParams();

  const { data: user } = useGetUserLoggin();
  const { data: loans } = useGetLoanByOwner();

  let loanUrlParam = loans?.find(loan => loan._id === loanIdParam);
  const loansNotDead = loans?.filter(loan => loan.state !== 'dead');
  let loanSelect = loanUrlParam || (loansNotDead && loansNotDead[loansNotDead.length - 1]) || loans?.[loans.length - 1];

  const [isCreateTaskResponse, setCreateTaskResponse] = useState<string>('idle');

  const [loanId, setLoanId] = useState<string>('');
  const [loan, setLoan] = useState<any | undefined>();
  const [filter, setFilter] = useState<string>('all');
  const [phaseSelect, setPhaseSelect] = useState<string>('approval');
  const [isUpdateDashboard, setUpdateDashboard] = useState<boolean>();

  const { data: tasks, reexecute: getTasksByLoan } = useGetTasksByLoan({
    variables: { loanId, state: filter },
    pause: true
  });

  // Query separada para obtener todas las tareas sin filtrar (para cardAddDocs)
  const { data: allTasks, reexecute: getAllTasks } = useGetTasksByLoan({
    variables: { loanId, state: undefined },
    pause: true
  });

  const { card: cardPropertyAccess, dataTask: propertyAccess } = useCardPropertyAccess({ user, tasks });
  const { card: cardEntityGuarantor, dataTask: entityGuarantor } = useCardEntityGuarantor({ tasks });
  const { card: cardMinimumLiquidity, dataTask: minimumLiquidity } = useCardMinimumLiquidity({ tasks });
  const { card: cardTrackRecord, dataTask: trackRecord } = useCardTrackRecord({ tasks, loan: loanSelect });
  const { card: cardEnttiyDocs, dataTask: entityDocs } = useCardEntityDocs({
    tasks,
    loan: loanSelect,
    ownerSelect: ownerSelect || '',
  });
  const { card: cardInvestment, dataTask: investment } = useCardInvestment({ tasks, loan: loanSelect, trackRecord });
  const { card: cardConstruction, dataTask: construction } = useCardConstruction({ tasks });
  const { card: cardTitleCompany, dataTask: titleCompany } = useCardTitleCompany({ tasks });
  const { card: cardInsurance, dataTask: insurance, transformState: insuranceTransformState } = useCardInsurance({ tasks, loan: loanSelect });
  const { card: cardPurchase, dataTask: purchase } = useCardPurchase({ tasks });
  const { card: cardPayoff, dataTask: payoff } = useCardPayoff({ tasks });
  const { card: cardPayAppraisal, dataTask: payAppraisal } = useCardPayAppraisal({ tasks });
  const { card: cardDSCR, dataTask: dscr } = useCardDSCRQuestionsAgreements({ tasks, loan: loanSelect, trackRecord });
  const { cards: cardsNewTasks } = useCardNewTask({ tasks });
  // Obtener cardAddDocs de allTasks para que siempre esté disponible independientemente del filtro
  const cardAddDocs = allTasks?.find((item: any) => item.key === 'add-docs');

  const _taskCards = [
    [cardPropertyAccess, propertyAccess],
    [cardEntityGuarantor, entityGuarantor],
    [cardMinimumLiquidity, minimumLiquidity],
    [cardTrackRecord, trackRecord],
    [cardEnttiyDocs, entityDocs],
    [cardInvestment, investment],
    [cardConstruction, construction],
    [cardTitleCompany, titleCompany],
    [cardInsurance, insurance],
    [cardPurchase, purchase],
    [cardPayoff, payoff],
    [cardPayAppraisal, payAppraisal],
    [cardDSCR, dscr]
  ];

  cardsNewTasks?.map((newTask: any) => {
    let isCard = RulesTasksCards.find(item => item.key === newTask.data._id);
    let _newTask = {
      key: newTask.data._id,
      modalKey: newTask.data._id,
      name: newTask.data.name,
      card: newTask,
      data: newTask.data,
      phase: [newTask.data?.phase],
      phaseProgress: newTask.data?.phase,
      state: newTask.data?.state
    };

    if (!isCard)
      RulesTasksCards.push(_newTask as any);
    else {
      isCard.state = _newTask.state;
      isCard.data = _newTask.data;
      isCard.card = newTask;
    }
  })

  for (const rules of RulesTasksCards) {
    const findTaskCard = _taskCards.find(item => item && item[0]?.key === rules.key);

    if (findTaskCard) {
      const [card, data] = findTaskCard;
      if (card) rules.card = card;
      if (data) rules.data = data as Task;
      rules.state = rules.state !== TaskState.Approved ? data?.state : rules.state;
    }
  }
  // Sync insurance visual state override so filters and loan progress match the card
  if (insuranceTransformState) {
    const insuranceRule = RulesTasksCards.find(item => item.key === ModuleKeys.INSURANCE);
    if (insuranceRule) insuranceRule.state = insuranceTransformState;
  }

  const filteredRulesTasksCards = RulesTasksCards.filter(item => !item.data?.hidden && item.data)

  useEffect(() => {
    if (loans && loans.length > 0 && loanSelect) {
      setLoanId(loanSelect._id);
      setLoan(loanSelect);
    }
  }, [loans, loanIdParam]);

  // Sync loan context into Sentry and runtime when loan changes
  useEffect(() => {
    updateLoanContextInSentry(loanSelect);
  }, [loanSelect?._id]);

  useEffect(() => {
    if (loanId || isUpdateDashboard) {
      getTasksByLoan({ requestPolicy: 'network-only' });
      getAllTasks({ requestPolicy: 'network-only' });
    }

    if (isUpdateDashboard) setUpdateDashboard(false);
  }, [loanId, isUpdateDashboard, filter]);

  useEffect(() => {
    setFilter('all');
  }, [phaseSelect]);

  useEffect(() => {
    if (isCreateTaskResponse !== 'idle') {
      setFilter('all');
      getTasksByLoan({ requestPolicy: 'network-only' });
      getAllTasks({ requestPolicy: 'network-only' });
    }
  }, [isCreateTaskResponse])

  const getTasksCards = () => {
    const taskCards: CardLoanTasksProps[] = [],
      tasksFilter = [],
      tasksAll = [],
      phaseSection: any[] = [
        { key: Phases.APPROVAL, label: 'Approval Docs & Term Sheet', tasks: [], state: 'DONE' },
        { key: Phases.DUE_DILIGENCE, label: 'Property Evaluation & Due Diligence', tasks: [], state: 'DONE' },
        { key: Phases.FINAL, label: 'Final Approval', tasks: [], state: 'DONE' },
        { key: Phases.CLOSING, label: 'Closing', tasks: [], state: 'DONE' },
      ];

    if (!filteredRulesTasksCards)
      return [];

    for (const taskCard of filteredRulesTasksCards) {
      const isActionRequired =
        taskCard.state === TaskState.ComingSoon ||
        taskCard.state === TaskState.Draft ||
        taskCard.state === TaskState.Incomplete ||
        taskCard.state === TaskState.RequestError;

      const includeParamLoan = (!taskCard.loanType || taskCard.loanType.includes(loan?.type)) &&
        (!taskCard.loanPurposes || taskCard.loanPurposes.includes(loan?.purpose));

      const shouldInclude =
        taskCard.card &&
        (filter === 'all' ||
          (filter === 'ACTION_REQUIRED' && isActionRequired) ||
          (filter === 'REVIEW' &&
            taskCard.state === TaskState.Review) ||
          (filter !== 'REVIEW' &&
            filter !== 'ACTION_REQUIRED' &&
            taskCard.state === filter)) &&
        (!taskCard.filters || taskCard.filters.includes(filter)) &&
        (!taskCard.loanType || taskCard.loanType.includes(loan?.type)) &&
        (!taskCard.loanPurposes ||
          taskCard.loanPurposes.includes(loan?.purpose));

      if (shouldInclude) {
        tasksFilter.push(taskCard.data);
        taskCard.phase.forEach((phaseKey: string) => {
          const phaseObj = phaseSection.find((item) => item.key === phaseKey);
          if (phaseObj) {
            phaseObj.tasks.push(taskCard.card);
            if (taskCard.state !== TaskState.Approved) {
              phaseObj.state = 'DO';
            }
          }
        });
      }
      
      if (includeParamLoan && taskCard.data) {
        const taskData = taskCard.key === ModuleKeys.INSURANCE
          ? { ...taskCard.data, state: insuranceTransformState ?? taskCard.state }
          : taskCard.data;
        tasksAll.push(taskData);
      }
    }

    for (const phase of phaseSection) {
      const isDoLater = phase.tasks.every((taskCard: any) =>
        taskCard.state === TaskState.ComingSoon
      )

      if (isDoLater)
        phase.state = 'DO_LATER'
    }

    // Añadir cardAddDocs a tasksAll si existe (independientemente del filtro)
    if (cardAddDocs && cardAddDocs.key) {
      tasksAll.push(cardAddDocs as Task);
    }

    return { taskCards, phaseSection, tasksFilter: tasksFilter, tasksAll };
  }

  const { tasksAll, tasksFilter, taskCards, phaseSection }: any = getTasksCards();

  // Inicializar ownerId desde Entity Guarantors para que Entity Docs esté habilitada
  // aunque la sección Approval Docs esté cerrada
  // MISMO CODIGO QUE ESTA EN EntityGuarantorCreation.tsx linea 117 useEffect
  const initialOwnerId = React.useMemo(() => {
    if (!tasks?.length) return undefined;
    const entityGuarantorTask = tasks.find((t: any) => t.key === ModuleKeys.ENTITY_GUARANTORS);
    const taskResponses = entityGuarantorTask?.responses;
    if (!taskResponses?.length) return undefined;

    const step1 = taskResponses.find((item: any) => item.step === 1);
    let ownerId = step1?.metadata?.ownerId;

    const step2 = taskResponses?.filter(
      (item: any) =>
        item.step === 2 &&
        item.state !== TaskState.Cache &&
        item.key !== 'confirm-persons'
    ) || [];

    if (!step1?.metadata?.ownerId && step2[0]) {
      ownerId = step2[0]?.metadata?.ownerId;
    }
    if (ownerId && step2.length > 0 && !step2.find((item: any) => item.metadata?.ownerId === ownerId)) {
      const newOwner = step2.find((item: any) => item.metadata?.ownerId !== ownerId);
      return newOwner?.metadata?.ownerId || newOwner?.metadata?._id;
    }
    return ownerId;
  }, [tasks]);

  const getPhaseNow = () => {
    const approvalDone = filteredRulesTasksCards.filter(taskCard =>
      taskCard.phase.includes(Phases.APPROVAL) &&
      (!taskCard.loanType || taskCard.loanType?.includes(loan?.type)) &&
      (!taskCard.loanPurposes || taskCard.loanPurposes?.includes(loan?.purpose))
    ).every(taskCard => taskCard.state === TaskState.Approved);

    if (approvalDone) return { label: PhasesLabel[Phases.DUE_DILIGENCE], key: Phases.DUE_DILIGENCE };

    const isApproval = filteredRulesTasksCards.find(taskCard =>
      taskCard.phase.includes(Phases.APPROVAL) &&
      (!taskCard.loanType || taskCard.loanType?.includes(loan.type)) &&
      (!taskCard.loanPurposes || taskCard.loanPurposes?.includes(loan?.purpose)) &&
      taskCard.state !== TaskState.Draft
    );

    if (isApproval) return { label: PhasesLabel[Phases.APPROVAL], key: Phases.APPROVAL };

    const isPreApproval = filteredRulesTasksCards.find(taskCard =>
      taskCard.phase.includes(Phases.PRE_APPROVAL) &&
      taskCard.state !== TaskState.Draft
    );

    if (isPreApproval) return { label: PhasesLabel[Phases.PRE_APPROVAL], key: Phases.PRE_APPROVAL };

    return { label: PhasesLabel[Phases.APPROVAL], key: Phases.APPROVAL };

  }

  const phaseNow = getPhaseNow();

  return {
    loans,
    loanSelect,
    tasks,

    isCreateTaskResponse,
    setCreateTaskResponse,

    isUpdateDashboard,
    setUpdateDashboard,

    phaseNow,
    phaseSelect,
    setPhaseSelect,

    filter,
    setFilter,

    rulesTasksCards: filteredRulesTasksCards,
    rulesTasksCardsAll: RulesTasksCards, // Versión sin filtrar para no perder las tasks cuando se filtran
    tasksAll,
    phaseSection,

    initialOwnerId,
  }
};
export default useDashboard;